/**
 * P6 (E1 weather rider) — run-start conditions, served by our own
 * `/api/weather` (WeatherKit server-side; Open-Meteo removed 2026-08-06,
 * task 39 — its free API is non-commercial only).
 *
 * Fetched ONCE at run start (the first GPS fix) and shown on the Live
 * Activity + run summary. ENHANCEMENT ONLY: every failure — flag off,
 * offline, timeout, non-200, bad shape — resolves to null and the weather
 * line simply doesn't appear. Weather never blocks or delays a run.
 *
 * Units: °F + mph, matching run-spike's imperial-hardcoded stats (miles,
 * feet). Not persisted — ephemeral, for the current run's summary + LA.
 */

import { apiUrl } from '../apiBase';
import { getServerFlags } from '../supabase';

export interface RunWeather {
  tempF: number; // whole degrees
  windMph: number; // whole mph
  windDir: number; // meteorological degrees (0/360 = wind FROM the north)
  /** B-24 — WMO weather_code mapped to a short lowercase word ("overcast").
   *  Optional enhancement-on-the-enhancement: absent when the code is
   *  missing or unknown; never fails the fetch. */
  condition?: string;
}

const TIMEOUT_MS = 4000;

/**
 * ⚖️ PROVIDER SEAM — apple-data-services lane (task 39; Open-Meteo REMOVED
 * 2026-08-06). Open-Meteo's free API is NON-COMMERCIAL ONLY — the license is
 * the point, and a "fallback" to it would still be commercial use, so there
 * is no fallback: `weather_apple_v1` OFF now means NO weather line (the
 * enhancement simply doesn't render), ON means our `/api/weather`
 * (WeatherKit server-side, same RunWeather shape, null on any failure).
 */
async function fetchAppleWeather(lat: number, lng: number): Promise<RunWeather | null> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(
      apiUrl(`/api/weather?lat=${lat.toFixed(4)}&lng=${lng.toFixed(4)}`),
      { signal: ctrl.signal },
    );
    if (!res.ok) return null;
    const json = (await res.json()) as { weather?: RunWeather | null };
    const w = json?.weather;
    if (!w || typeof w.tempF !== 'number' || typeof w.windMph !== 'number') return null;
    return w;
  } catch {
    return null; // enhancement only — same contract as the Open-Meteo path
  } finally {
    clearTimeout(t);
  }
}

export async function fetchRunWeather(lat: number, lng: number): Promise<RunWeather | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (!getServerFlags()['weather_apple_v1']) {
    // Flag OFF = no provider at all (the Open-Meteo path is gone — seam
    // note above). Weather is an enhancement; absent is a valid state.
    return null;
  }
  return fetchAppleWeather(lat, lng);
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

/** 8-point compass from meteorological degrees (0/360 = N, wraps both ways). */
export function compassFromDegrees(deg: number): string {
  if (!Number.isFinite(deg)) return '';
  const norm = (((deg % 360) + 360) % 360);
  return COMPASS[Math.round(norm / 45) % 8];
}

/** Summary line: `58°F · 7 mph NE`. */
export function formatWeatherSummary(w: RunWeather): string {
  const dir = compassFromDegrees(w.windDir);
  return `${w.tempF}°F · ${w.windMph} mph${dir ? ` ${dir}` : ''}`;
}

/**
 * Ask 3 (Claude Design return 2026-10-19, frame 3d): the Run sheet's idle Weather row
 * value, `57° overcast · SE 6 mph`. The condition word is optional (absent when the
 * code was unknown: `57° · SE 6 mph`) and so is the direction (`57° overcast · 6 mph`).
 */
export function formatIdleWeatherLine(w: RunWeather): string {
  const dir = compassFromDegrees(w.windDir);
  const wind = `${dir ? `${dir} ` : ''}${w.windMph} mph`;
  return `${w.tempF}°${w.condition ? ` ${w.condition}` : ''} · ${wind}`;
}

/** Live Activity line (tighter — island space is scarce): `58° · 7mph NE`. */
export function formatWeatherLA(w: RunWeather): string {
  const dir = compassFromDegrees(w.windDir);
  return `${w.tempF}° · ${w.windMph}mph${dir ? ` ${dir}` : ''}`;
}
